import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card } from '../common/Card'
import { theme } from '../../styles/theme'
import { formatRelativeTime } from '../../utils/format'
import type { Activity } from '../../types'

export const ActivityFeed: React.FC = () => {
  const navigate = useNavigate()
  const [activities, setActivities] = useState<Activity[]>([])
  const [filter, setFilter] = useState<'all' | 'query' | 'report' | 'market'>('all')

  useEffect(() => {
    // Mock activity data - in production, fetch from API
    setActivities([
      {
        id: '1',
        type: 'query',
        description: 'Asked about German market opportunities',
        timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
        userId: 'user-123',
      },
      {
        id: '2',
        type: 'report',
        description: 'Generated Q3 2024 quarterly report',
        timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
        userId: 'user-123',
      },
      {
        id: '3',
        type: 'market_view',
        description: 'Viewed France market details',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        userId: 'user-123',
      },
      {
        id: '4',
        type: 'strategy_create',
        description: 'Created expansion strategy for EU markets',
        timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
        userId: 'user-123',
      },
      {
        id: '5',
        type: 'export',
        description: 'Exported market analysis to PDF',
        timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        userId: 'user-123',
      },
      {
        id: '6',
        type: 'query',
        description: 'Analyzed VAT implications for Italy',
        timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        userId: 'user-123',
      },
    ])
  }, [])

  const filteredActivities = filter === 'all'
    ? activities
    : activities.filter(a => {
        if (filter === 'query') return a.type === 'query'
        if (filter === 'report') return a.type === 'report'
        if (filter === 'market') return a.type === 'market_view'
        return true
      })

  return (
    <Card>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
      }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: 600,
          margin: 0,
          color: theme.colors.textPrimary,
        }}>
          Recent Activity
        </h2>

        {/* Filter Buttons */}
        <div style={{
          display: 'flex',
          gap: '8px',
          borderRadius: '8px',
          padding: '4px',
          backgroundColor: theme.colors.background,
        }}>
          {['all', 'query', 'report', 'market'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f as any)}
              style={{
                padding: '6px 12px',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                backgroundColor: filter === f ? '#FFFFFF' : 'transparent',
                color: filter === f ? theme.colors.primary : theme.colors.textSecondary,
                boxShadow: filter === f ? theme.shadows.sm : 'none',
                transition: 'all 0.2s',
              }}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Activity Timeline */}
      <div style={{
        position: 'relative',
      }}>
        {/* Timeline Line */}
        <div style={{
          position: 'absolute',
          left: '20px',
          top: '16px',
          bottom: '16px',
          width: '2px',
          backgroundColor: theme.colors.border,
        }} />

        {/* Activity Items */}
        {filteredActivities.map((activity) => (
          <ActivityItem key={activity.id} activity={activity} />
        ))}

        {filteredActivities.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: theme.colors.textSecondary,
          }}>
            No activities found
          </div>
        )}
      </div>

      {/* View All Button */}
      {activities.length > 6 && (
        <div style={{ marginTop: '16px' }}>
          <button
            onClick={() => navigate('/activity')}
            style={{
              width: '100%',
              padding: '12px',
              background: 'none',
              border: `1px solid ${theme.colors.border}`,
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 500,
              color: theme.colors.primary,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = theme.colors.background
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'none'
            }}
          >
            View All Activity →
          </button>
        </div>
      )}
    </Card>
  )
}

const ActivityItem: React.FC<{ activity: Activity }> = ({ activity }) => {
  const [isHover, setIsHover] = useState(false)

  const icons: Record<Activity['type'], string> = {
    query: '🔍',
    report: '📊',
    market_view: '🌍',
    strategy_create: '🎯',
    export: '📥',
  }

  const colors: Record<Activity['type'], string> = {
    query: theme.colors.purple,
    report: theme.colors.primary,
    market_view: theme.colors.success,
    strategy_create: theme.colors.warning,
    export: theme.colors.textSecondary,
  }

  return (
    <div
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      style={{
        position: 'relative',
        paddingLeft: '56px',
        paddingBottom: '20px',
        cursor: 'pointer',
      }}
    >
      {/* Icon */}
      <div style={{
        position: 'absolute',
        left: '8px',
        top: '0',
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        backgroundColor: isHover ? colors[activity.type] : theme.colors.background,
        border: `2px solid ${colors[activity.type]}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        transition: 'all 0.2s',
        zIndex: 1,
        boxShadow: isHover ? theme.shadows.sm : 'none',
      }}>
        {icons[activity.type]}
      </div>

      {/* Content */}
      <div style={{
        padding: '8px 12px',
        borderRadius: '8px',
        backgroundColor: isHover ? theme.colors.background : 'transparent',
        transition: 'background 0.2s',
      }}>
        <div style={{
          fontSize: '14px',
          color: theme.colors.textPrimary,
          marginBottom: '4px',
          fontWeight: 500,
        }}>
          {activity.description}
        </div>
        <div style={{
          fontSize: '12px',
          color: theme.colors.textSecondary,
        }}>
          {formatRelativeTime(activity.timestamp)}
        </div>
      </div>
    </div>
  )
}
